import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Editor } from 'primereact/editor';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useTermsPage, useUpdateTermsPage } from '../hooks/useCmsPages';
import Swal from 'sweetalert2';

const Terms = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    data: [
      {
        title: { ar: '', en: '' },
        text: { ar: '', en: '' }
      }
    ]
  });

  // Fetch terms data using React Query
  const {
    data: termsData,
    isLoading,
    error,
  } = useTermsPage();
  console.log(termsData);

  // Update terms mutation
  const updateTermsMutation = useUpdateTermsPage();

  // Initialize edited data when terms data is loaded
  React.useEffect(() => {
    if (termsData?.data && !isEditing) {
      setEditedData(termsData);
    }
  }, [termsData, isEditing]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({ ...termsData });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({ ...termsData });
  };

  const handleSave = async () => {
    try {
      await updateTermsMutation.mutateAsync(editedData);
      setIsEditing(false);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Terms & conditions updated successfully!',
        timer: 2000,
        showConfirmButton: false,
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#f59e0b] text-white font-bold py-2 px-4 rounded',
        }
      });
    } catch (error) {
      console.error('Error updating terms & conditions:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to update terms & conditions',
        confirmButtonText: 'OK',
        background: '#ffffff',
        color: '#333',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        }
      });
    }
  };

  const handleTitleChange = (lang, value) => {
    setEditedData(prev => ({
      ...prev,
      data: [
        {
          ...prev.data[0],
          title: {
            ...prev.data[0].title,
            [lang]: value
          }
        }
      ]
    }));
  };

  const handleTextChange = (lang, value) => {
    setEditedData(prev => ({
      ...prev,
      data: [
        {
          ...prev.data[0],
          text: {
            ...prev.data[0].text,
            [lang]: value
          }
        }
      ]
    }));
  };

  // Handle loading state
  if (isLoading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="Terms & Conditions"
            description="Manage terms and conditions content"
            user={user}
          />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="Terms & Conditions"
          description="Manage terms and conditions content"
        />

        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Content Management</h2>
              <div className="flex space-x-3">
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-md transition duration-200"
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md transition duration-200"
                      disabled={updateTermsMutation.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={updateTermsMutation.isPending}
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md transition duration-200 disabled:opacity-50"
                    >
                      {updateTermsMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 space-y-8">
            {/* English Content */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Content</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={editedData.data?.[0]?.title?.en || ''}
                    onChange={(e) => handleTitleChange('en', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter English title"
                  />
                ) : (
                  <div className="text-gray-900 font-medium">{termsData?.data?.[0]?.title?.en || 'No title set'}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                {isEditing ? (
                  <div className="border border-gray-300 rounded-md">
                    <Editor
                      value={editedData.data?.[0]?.text?.en || ''}
                      onTextChange={(e) => handleTextChange('en', e.htmlValue)}
                      style={{ height: '300px' }}
                    />
                  </div>
                ) : (
                  <div 
                    className="prose max-w-none p-4 border border-gray-200 rounded-md bg-gray-50"
                    dangerouslySetInnerHTML={{ __html: termsData?.data?.[0]?.text?.en || 'No content set' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Terms; 